import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ContentProvider } from "./contexts/ContentContext";
import Lenis from "lenis";
import Header from "./components/Layout/Header";
import Footer from "./components/Layout/Footer";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import SkipLink from "./components/SkipLink";

// Lazy-loaded pages
const HomePage = lazy(() => import("./pages/HomePage"));
const HeizungPage = lazy(() => import("./pages/HeizungPage"));
const BadSanitaerPage = lazy(() => import("./pages/BadSanitaerPage"));
const RegenerativeEnergienPage = lazy(() => import("./pages/RegenerativeEnergienPage"));
const SmartHomePage = lazy(() => import("./pages/SmartHomePage"));
const KlimaPage = lazy(() => import("./pages/KlimaPage"));
const PartnerPage = lazy(() => import("./pages/PartnerPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const HeizungsrechnerPage = lazy(() => import("./pages/HeizungsrechnerPage"));
const KontaktPage = lazy(() => import("./pages/KontaktPage"));
const KarrierePage = lazy(() => import("./pages/KarrierePage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Smooth scroll
const SmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);
  return null;
};

const AppLayout = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return <Suspense fallback={null}><Routes><Route path="/admin" element={<AdminPage />} /></Routes></Suspense>;
  return (
    <>
      <SkipLink />
      <Header />
      <main id="main-content" className="main-content" role="main">
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/heizung" element={<HeizungPage />} />
            <Route path="/bad-sanitaer" element={<BadSanitaerPage />} />
            <Route path="/regenerative-energien" element={<RegenerativeEnergienPage />} />
            <Route path="/smart-home" element={<SmartHomePage />} />
            <Route path="/klima" element={<KlimaPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/heizungsrechner" element={<HeizungsrechnerPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/karriere" element={<KarrierePage />} />
            <Route path="/karriere/:jobId" element={<JobDetailPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <ContentProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollToTop />
          <AppLayout />
        </BrowserRouter>
      </ContentProvider>
      </HelmetProvider>
    </div>
  );
}
export default App;
